import type { TimeSeriesItem } from "~/modules/asset_timeseries_v4/types";
import { RWA_COLORS } from "~/styles/colors";
import type { ScreenerAsset } from "./types";

// // // //

export type PerformancePeriod = "1M" | "3M" | "YTD" | "1Y";
export type PerformanceGroup = "assetClass" | "manager";

export interface PerformanceChartSeries {
    color: string;
    data: Array<[number, number]>;
    id: number;
    name: string;
}

export interface PerformanceChartData {
    series: PerformanceChartSeries[];
}

const MAX_MANAGER_SERIES = 10;

function getLatestTimestamp(series: TimeSeriesItem[]): number | null {
    let latestTimestamp: number | null = null;

    for (const item of series) {
        for (const [date] of item.points) {
            const timestamp = Date.parse(date);
            if (
                Number.isFinite(timestamp) &&
                (latestTimestamp === null || timestamp > latestTimestamp)
            ) {
                latestTimestamp = timestamp;
            }
        }
    }

    return latestTimestamp;
}

function subtractUtcMonths(timestamp: number, months: number): number {
    const date = new Date(timestamp);
    const day = date.getUTCDate();

    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() - months);

    const lastDayOfMonth = new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0),
    ).getUTCDate();
    date.setUTCDate(Math.min(day, lastDayOfMonth));

    return date.getTime();
}

function getPeriodStartTimestamp(
    latestTimestamp: number,
    period: PerformancePeriod,
): number {
    if (period === "1M") {
        return subtractUtcMonths(latestTimestamp, 1);
    }

    if (period === "3M") {
        return subtractUtcMonths(latestTimestamp, 3);
    }

    const latestDate = new Date(latestTimestamp);
    if (period === "YTD") {
        return Date.UTC(latestDate.getUTCFullYear(), 0, 1);
    }

    return Date.UTC(
        latestDate.getUTCFullYear() - 1,
        latestDate.getUTCMonth(),
        latestDate.getUTCDate(),
    );
}

/**
 * dedupePerformanceGroups
 * The timeseries API groups by id AND name, so an asset class that has been
 * renamed can come back twice under one id (e.g. 34 "Public Equity" and 34
 * "Stocks"). Duplicate ids crash Highcharts' one-to-one series update and
 * collide as React keys, so keep a single item per id — preferring the one
 * whose group name matches the current asset class name, otherwise the first.
 */
function dedupePerformanceGroups(
    performanceByGroup: TimeSeriesItem[],
    assetClasses: Map<number, { name: string }> | null,
): TimeSeriesItem[] {
    const itemsByID = new Map<number, TimeSeriesItem>();

    for (const item of performanceByGroup) {
        const existing = itemsByID.get(item.group.id);
        const preferredName = assetClasses?.get(item.group.id)?.name;
        const isPreferred =
            preferredName !== undefined &&
            item.group.name === preferredName &&
            existing?.group.name !== preferredName;

        if (!existing || isPreferred) {
            itemsByID.set(item.group.id, item);
        }
    }

    return Array.from(itemsByID.values());
}

export function getPerformanceChartData(
    performanceByGroup: TimeSeriesItem[],
    assets: ScreenerAsset[] | null,
    period: PerformancePeriod,
): PerformanceChartData {
    const assetClasses = assets
        ? new Map(
              assets.map((asset) => [asset.assetClass.id, asset.assetClass]),
          )
        : null;
    const performanceGroups = dedupePerformanceGroups(
        performanceByGroup,
        assetClasses,
    );

    const latestTimestamp = getLatestTimestamp(performanceGroups);
    if (latestTimestamp === null) {
        return { series: [] };
    }

    const periodStartTimestamp = getPeriodStartTimestamp(
        latestTimestamp,
        period,
    );

    const series = performanceGroups
        .map<PerformanceChartSeries | null>((item) => {
            const assetClass = assetClasses?.get(item.group.id);
            if (assetClasses && !assetClass) {
                return null;
            }

            const points = item.points
                .map<[number, number]>(([date, value]) => [
                    Date.parse(date),
                    Number(value),
                ])
                .filter(
                    ([timestamp, value]) =>
                        Number.isFinite(timestamp) &&
                        Number.isFinite(value) &&
                        value > 0 &&
                        timestamp <= latestTimestamp,
                )
                .sort((a, b) => a[0] - b[0]);

            let baselinePoint: [number, number] | undefined;
            for (let index = points.length - 1; index >= 0; index--) {
                if (points[index][0] <= periodStartTimestamp) {
                    baselinePoint = points[index];
                    break;
                }
            }
            baselinePoint ??= points.find(
                ([timestamp]) => timestamp >= periodStartTimestamp,
            );

            if (!baselinePoint) {
                return null;
            }

            const normalizedPoints = points
                .filter(([timestamp]) => timestamp >= baselinePoint[0])
                .map<[number, number]>(([timestamp, value]) => [
                    timestamp,
                    (value / baselinePoint[1] - 1) * 100,
                ]);

            if (normalizedPoints.length < 2) {
                return null;
            }

            return {
                color:
                    assetClass?.color_hex ??
                    item.group.color ??
                    RWA_COLORS.slate["500"],
                data: normalizedPoints,
                id: assetClass?.id ?? item.group.id,
                name: assetClass?.name ?? item.group.name,
            };
        })
        .filter((item): item is PerformanceChartSeries => item !== null)
        .sort((a, b) => {
            const aLatest = a.data[a.data.length - 1]?.[1] ?? 0;
            const bLatest = b.data[b.data.length - 1]?.[1] ?? 0;
            return bLatest - aLatest;
        });

    return { series };
}

export function getGroupedPerformanceChartData(props: {
    assets: ScreenerAsset[];
    group: PerformanceGroup;
    performanceByAssetClass: TimeSeriesItem[];
    performanceByManager: TimeSeriesItem[];
    period: PerformancePeriod;
}): PerformanceChartData {
    const {
        assets,
        group,
        performanceByAssetClass,
        performanceByManager,
        period,
    } = props;
    const isAssetClassGroup = group === "assetClass";
    const data = getPerformanceChartData(
        isAssetClassGroup
            ? performanceByAssetClass
            : performanceByManager.filter((item) => item.group.id > 0),
        isAssetClassGroup ? assets : null,
        period,
    );

    return isAssetClassGroup
        ? data
        : { series: data.series.slice(0, MAX_MANAGER_SERIES) };
}
